<div class="bn-accounts">
  @if (!store.loading() && !store.error() && store.banner(); as banner) {
    <div class="bn-accounts-banner" role="alert" data-testid="accounts-banner">
      <i
        class="pi pi-exclamation-circle bn-accounts-banner__icon"
        aria-hidden="true"
      ></i>
      <div>
        <p class="bn-accounts-banner__headline">{{ banner.headline }}</p>
        <p class="bn-accounts-banner__body">{{ banner.body }}</p>
      </div>
    </div>
  }

  <bn-collapsible-section
    title="Account Inventory"
    icon="pi pi-chart-pie"
    ariaLabel="Account Inventory"
  >
    @if (showTotal()) {
      <p-tag
        sectionHeaderExtra
        [value]="'Total accounts: ' + store.accounts().length"
        icon="pi pi-check-circle"
        severity="info"
        [rounded]="true"
        styleClass="bn-accounts-count"
        data-testid="accounts-total"
      />
    }

    @if (store.loading()) {
      <bn-accounts-skeleton />
    } @else if (store.error()) {
      <div class="bn-accounts__error" role="alert">
        <p>We couldn't load accounts right now.</p>
        <p-button
          label="Retry"
          severity="secondary"
          size="small"
          (onClick)="store.reload()"
        />
      </div>
    } @else if (!showingInventory()) {
      <div class="bn-accounts__empty" role="status">
        <i
          class="pi pi-credit-card text-4xl text-surface-300"
          aria-hidden="true"
        ></i>
        <p>Select a client to view their accounts.</p>
      </div>
    } @else if (store.accounts().length === 0) {
      <div
        class="bn-accounts__empty"
        role="status"
        data-testid="accounts-empty"
      >
        <p>No accounts on file for this client.</p>
      </div>
    } @else {
      <div class="bn-accounts-toolbar">
        <label
          class="bn-accounts-toolbar__field bn-accounts-toolbar__field--search"
        >
          <span class="bn-accounts-toolbar__label">Search</span>
          <span class="bn-accounts__search">
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              pInputText
              type="search"
              placeholder="Account, institution, last 4"
              aria-label="Search account, institution, last 4"
              [ngModel]="search()"
              (ngModelChange)="onSearchChange($event)"
              [disabled]="!!store.error() || store.loading()"
            />
          </span>
        </label>
        <div
          class="bn-accounts-toolbar__field bn-accounts-toolbar__field--status"
        >
          <span class="bn-accounts-toolbar__label">Status</span>
          <p-select
            [options]="statusOptions()"
            [ngModel]="statusFilter()"
            (ngModelChange)="onStatusChange($event)"
            optionLabel="label"
            optionValue="value"
            ariaLabel="Filter by status"
            [appendTo]="'body'"
            size="small"
            styleClass="bn-accounts-toolbar__select"
            data-testid="accounts-status-filter"
          />
        </div>
        <div
          class="bn-accounts-toolbar__field bn-accounts-toolbar__field--compact"
        >
          <span class="bn-accounts-toolbar__label">Type</span>
          <p-select
            [options]="typeOptions()"
            [ngModel]="typeFilter()"
            (ngModelChange)="onTypeChange($event)"
            optionLabel="label"
            optionValue="value"
            ariaLabel="Filter by type"
            [appendTo]="'body'"
            size="small"
            styleClass="bn-accounts-toolbar__select"
            data-testid="accounts-type-filter"
          />
        </div>
        <div
          class="bn-accounts-toolbar__field bn-accounts-toolbar__field--compact"
        >
          <span class="bn-accounts-toolbar__label">Group by</span>
          <p-select
            [options]="groupByOptions"
            [ngModel]="groupBy()"
            (ngModelChange)="onGroupByChange($event)"
            optionLabel="label"
            optionValue="value"
            ariaLabel="Group accounts"
            [appendTo]="'body'"
            size="small"
            styleClass="bn-accounts-toolbar__select"
            data-testid="accounts-group-by"
          />
        </div>
      </div>

      <p class="bn-accounts-toolbar__meta">
        Showing {{ visibleRows().length }} of
        {{ store.accounts().length }} accounts
        @if (!filtersDefault()) {
          <button
            type="button"
            class="bn-accounts-toolbar__reset"
            data-testid="accounts-reset-filters"
            (click)="resetFilters()"
          >
            Reset filters
          </button>
        }
      </p>

      @if (noSearchMatch()) {
        <div
          class="bn-accounts__empty"
          role="status"
          data-testid="accounts-no-match"
        >
          <p>No accounts match.</p>
        </div>
      } @else {
        <bds-scroll-region
          orientation="horizontal"
          styleClass="bn-accounts-table-scroll"
          ariaLabel="Account inventory table; scroll horizontally to view all columns"
        >
          <p-table
            [value]="tableRows()"
            sortMode="single"
            [customSort]="true"
            [sortField]="sortField() ?? undefined"
            [sortOrder]="sortOrder()"
            (sortFunction)="onSort($event)"
            [rowGroupMode]="showGroups() ? 'subheader' : undefined"
            [groupRowsBy]="showGroups() ? '__groupKey' : undefined"
            [styleClass]="
              showGroups()
                ? 'bn-accounts-table bn-accounts-table--grouped'
                : 'bn-accounts-table'
            "
            [stripedRows]="!showGroups()"
            ariaLabel="Client accounts"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="name" scope="col">
                  <span class="inline-flex items-center gap-1">
                    Account name
                    <p-sortIcon field="name" />
                  </span>
                </th>
                <th pSortableColumn="institution" scope="col">
                  <span class="inline-flex items-center gap-1">
                    Institution
                    <p-sortIcon field="institution" />
                  </span>
                </th>
                <th pSortableColumn="type" scope="col">
                  <span class="inline-flex items-center gap-1">
                    Type
                    <p-sortIcon field="type" />
                  </span>
                </th>
                <th pSortableColumn="status" scope="col">
                  <span class="inline-flex items-center gap-1">
                    Status
                    <p-sortIcon field="status" />
                  </span>
                </th>
                <th pSortableColumn="balance" scope="col" class="text-end">
                  <span class="inline-flex items-center gap-1 justify-end">
                    Balance
                    <p-sortIcon field="balance" />
                  </span>
                </th>
                <th scope="col" class="bn-accounts-table__details">
                  <span class="bn-accounts-table__details-label">Details</span>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="groupheader" let-row>
              <tr class="bn-accounts-group">
                <td colspan="6">
                  <span class="bn-accounts-group__label">{{
                    groupLabel(row)
                  }}</span>
                  <span class="bn-accounts-group__count">{{
                    groupCount(row)
                  }}</span>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>
                  <div class="bn-accounts-row__name">
                    {{ formatField(row.name) }}
                  </div>
                  <div class="bn-accounts-row__last4">
                    {{ formatLast4(row.idMasked) }}
                  </div>
                </td>
                <td>{{ formatField(row.institution) }}</td>
                <td>{{ formatField(row.type) }}</td>
                <td>
                  @if (row.status) {
                    <p-tag
                      [value]="row.status"
                      [severity]="statusSeverity(row.status)"
                      [rounded]="true"
                      styleClass="bn-accounts-status"
                    />
                  } @else {
                    —
                  }
                </td>
                <td class="bn-accounts-row__balance">
                  @if (isErrorStatus(row.status)) {
                    —
                  } @else {
                    {{ formatBalance(row.balance) }}
                  }
                </td>
                <td class="text-end">
                  <button
                    type="button"
                    class="bn-accounts-row__details"
                    [disabled]="!canOpenDetails(row)"
                    data-testid="accounts-view-details"
                    [attr.data-account-type]="row.type"
                    [attr.aria-label]="
                      'View details for ' + formatField(row.name)
                    "
                    (click)="openDetails(row)"
                  >
                    View details
                    <i class="pi pi-angle-right" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </bds-scroll-region>
      }
    }
  </bn-collapsible-section>

  @if (detail(); as data) {
    <bn-account-detail-dialog
      [data]="data"
      [visible]="detailOpen()"
      (visibleChange)="onDetailVisibleChange($event)"
    />
  }
</div>
